/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestTransactionsInterFace,
  TransactionsInitialProperties,
  TransactionsResponseInterface,
} from '../../interface/transactions-location-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Thunk to fetch transactions station list using the transaction list API.
 * @param {RequestTransactionsInterFace} data - The parameters for the transaction request.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<TransactionsResponseInterface>} Resolves with the transaction response.
 */
export const fetchTransactions = createAsyncThunk<
  TransactionsResponseInterface,
  RequestTransactionsInterFace
>('transactions/fetchTransactions', async (data: RequestTransactionsInterFace, { dispatch }) => {
  try {
    const sn = (data as any).chargingPointSn || (data as any).chargingpointsn;
    const accountCustomIdValue = (data as any).accountCustomId;
    const station = (data as any).stationId || (data as any).chargingStation;

    const queryData: Record<string, any> = { ...data };
    delete queryData.chargingpointsn;
    delete queryData.chargingPointSn;
    delete queryData.accountCustomId;
    delete queryData.chargingStation;
    delete queryData.stationId;

    if (sn) {
      queryData.chargingPointSn = Array.isArray(sn) ? sn : [sn];
    }
    if (accountCustomIdValue) {
      queryData.accountCustomId = Array.isArray(accountCustomIdValue)
        ? accountCustomIdValue
        : [accountCustomIdValue];
    }
    if (station) {
      queryData.stationId = Array.isArray(station) ? station : [station];
    }

    const queryParameters = removeEmptyKeys(queryData);
    const response = await cpoTransactionApi.fetchCpoTransactions(queryParameters);
    return {
      ...response.data,
      results: (response.data?.results || []).map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
        id: roleData._id || roleData.id,
      })),
    };
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message || 'Something went wrong!',
        code: error?.response?.data?.code || 200,
        status: true,
      }),
    );
    throw error;
  }
});

/**
 * Thunk to refresh transactions station list using the transaction list API.
 * @param {RequestTransactionsInterFace} data - The parameters for the refresh request.
 * @returns {Promise<TransactionsResponseInterface>} Resolves with the refreshed transaction response.
 */
export const refreshTransactions = createAsyncThunk<
  TransactionsResponseInterface,
  RequestTransactionsInterFace
>('transactions/refreshTransactions', async (data: RequestTransactionsInterFace) => {
  const sn = (data as any).chargingPointSn || (data as any).chargingpointsn;
  const accountCustomIdValue = (data as any).accountCustomId;
  const station = (data as any).stationId || (data as any).chargingStation;

  const queryData: Record<string, any> = { ...data };
  delete queryData.chargingpointsn;
  delete queryData.chargingPointSn;
  delete queryData.accountCustomId;
  delete queryData.chargingStation;
  delete queryData.stationId;

  if (sn) {
    queryData.chargingPointSn = Array.isArray(sn) ? sn : [sn];
  }
  if (accountCustomIdValue) {
    queryData.accountCustomId = Array.isArray(accountCustomIdValue)
      ? accountCustomIdValue
      : [accountCustomIdValue];
  }
  if (station) {
    queryData.stationId = Array.isArray(station) ? station : [station];
  }

  const queryParameters = removeEmptyKeys(queryData);
  const response = await cpoTransactionApi.fetchCpoTransactions(queryParameters);
  return {
    ...response.data,
    results: (response.data?.results || []).map((roleData: any) => ({
      ...roleData,
      date: ConvertUTCtoDate(roleData.createdAt),
      time: FormatUtcString(ConvertUTCtoHHMM(roleData.createdAt)),
      id: roleData._id || roleData.id,
    })),
  };
});

/**
 * Thunk to fetch transactions station filters.
 * @param {object} [payload] - The payload object.
 * @param {string} [payload.chargingStation] - Optional charging station.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<CommonFilterProperties[]>} Resolves with array of filter properties.
 */
export const fetchTransactionsFilters = createAsyncThunk<
  CommonFilterProperties[],
  { chargingStation?: string } | undefined
>('transactions/fetchTransactionsFilters', async (_payload, { dispatch }) => {
  try {
    const response = await cpoTransactionApi.fetchCpoTransactionFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
      response.data,
    ).filter((filter) => filter.type !== filterType.DATE_RANGE);
    return updatedResponse;
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message || 'Something went wrong!',
        code: error?.response?.data?.code || 200,
        status: true,
      }),
    );
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: TransactionsInitialProperties = {
  transactions: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for managing station transactions state.
 */
export const TransactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    /**
     * Update By Transaction filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateTransactionsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableTransactionFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<TransactionsInitialProperties>) => {
    builder
      .addCase(fetchTransactions.pending, (state: TransactionsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchTransactions.fulfilled,
        (
          state: TransactionsInitialProperties,
          action: PayloadAction<TransactionsResponseInterface>,
        ) => {
          state.loading = false;
          state.transactions = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchTransactions.rejected, (state: TransactionsInitialProperties) => {
        state.loading = false;
      });
    builder.addCase(
      refreshTransactions.fulfilled,
      (
        state: TransactionsInitialProperties,
        action: PayloadAction<TransactionsResponseInterface>,
      ) => {
        state.transactions = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchTransactionsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchTransactionsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload].filter(
            (object, index, self) =>
              index === self.findIndex((t) => t.key === object.key) &&
              object.type !== filterType.DATE_RANGE,
          );
        },
      )
      .addCase(fetchTransactionsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateTransactionsFilters, disableTransactionFiltersApplied, setSortData } =
  TransactionsSlice.actions;
export default TransactionsSlice.reducer;
